// strv-standard: theme v1.0.0
// Master copy lives in strvcom/strv-it-hub /standard. Copy unchanged.
//
// Themes: 'dark' (default) and 'light' (a deliberate choice, saved per
// browser). 'arcade' is a hidden easter egg: it is never saved, so a reload
// always drops it. Turned on by the Konami code or 8 fast clicks on the theme
// switch; the next click goes back to the normal toggle and resets the count.
//
// Inside the hub, a tool doesn't decide its theme: the hub sends it through
// the bridge (see bridge.js) and the tool calls applyTheme().

const STORAGE_KEY = 'strv-theme';
const NORMAL = ['dark', 'light'];

/** @typedef {'dark' | 'light' | 'arcade'} Theme */

/** @returns {'dark' | 'light'} the saved normal theme. Dark unless light was chosen. */
export function savedTheme() {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'light' ? 'light' : 'dark';
  } catch {
    return 'dark';
  }
}

/** @returns {Theme} */
export function currentTheme() {
  const t = document.documentElement.dataset.theme;
  return t === 'light' || t === 'arcade' ? t : 'dark';
}

/**
 * Put a theme on the page. Normal themes are saved unless `save` is false
 * (tools set it to false when the hub dictates the theme).
 * @param {Theme} theme
 * @param {{ save?: boolean }} [opts]
 */
export function applyTheme(theme, { save = true } = {}) {
  const t = theme === 'light' || theme === 'arcade' ? theme : 'dark';
  document.documentElement.dataset.theme = t;
  if (save && NORMAL.includes(t)) {
    try {
      localStorage.setItem(STORAGE_KEY, t);
    } catch {
      /* private mode: the choice just isn't remembered */
    }
  }
  document.dispatchEvent(new CustomEvent('strv:theme', { detail: { theme: t } }));
}

/** Apply the saved theme as early as possible (call before first paint). */
export function initTheme() {
  applyTheme(savedTheme(), { save: false });
}

const KONAMI = [
  'ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown',
  'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight', 'b', 'a',
];

/**
 * Wire a theme switch plus the arcade easter egg.
 *
 * @param {object} opts
 * @param {HTMLElement} [opts.switchEl] the element whose clicks toggle dark/light
 * @param {(theme: Theme) => void} [opts.onChange] called after every change
 * @param {number} [opts.clicks] fast clicks needed (default 8)
 * @param {number} [opts.windowMs] max gap between fast clicks (default 450 ms)
 * @returns {{ toggle: () => void, arcade: () => void, destroy: () => void }}
 */
export function installThemeSwitch({ switchEl, onChange, clicks = 8, windowMs = 450 } = {}) {
  let base = savedTheme(); // the normal theme arcade will hand back to
  let streak = 0;
  let lastClick = 0;
  let konami = 0;

  const set = (t, save) => {
    applyTheme(t, { save });
    onChange?.(t);
  };

  const arcade = () => {
    streak = 0;
    set('arcade', false);
  };

  const toggle = () => {
    const now = Date.now();
    streak = now - lastClick <= windowMs ? streak + 1 : 1;
    lastClick = now;

    if (currentTheme() === 'arcade') {
      // Leaving arcade: continue the normal cycle and restart the count.
      streak = 1;
      base = base === 'dark' ? 'light' : 'dark';
      set(base, true);
      return;
    }
    if (streak >= clicks) {
      arcade();
      return;
    }
    base = base === 'dark' ? 'light' : 'dark';
    set(base, true);
  };

  const onKey = (e) => {
    const target = e.target;
    if (target instanceof HTMLElement && target.closest('input, textarea, select, [contenteditable]')) return;
    const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;
    konami = key === KONAMI[konami] ? konami + 1 : key === KONAMI[0] ? 1 : 0;
    if (konami === KONAMI.length) {
      konami = 0;
      if (currentTheme() !== 'arcade') arcade();
    }
  };

  switchEl?.addEventListener('click', toggle);
  window.addEventListener('keydown', onKey);

  return {
    toggle,
    arcade,
    destroy() {
      switchEl?.removeEventListener('click', toggle);
      window.removeEventListener('keydown', onKey);
    },
  };
}
